import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import DashboardLayout from "../layout/Dashboard";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { ClientDetails } from "components/pages/clients/ClientDetails";
import { getUserDetails as getUserDetailsApi } from "../../api/clients/users";
import { Messages } from "../../components/layout/Messages/Messages";
import { ROUTE_CLIENT_USERS } from "../../config/routes";
import { DeleteClient } from "../../components/pages/clients/table/actionButtons/DeleteClient";
import { LockClient } from "../../components/pages/clients/table/actionButtons/LockClient";
import { UnlockClient } from "../../components/pages/clients/table/actionButtons/UnlockClient";
import { ResetPasswordClient } from "../../components/pages/clients/table/actionButtons/ResetPasswordClient";
import { history } from "../../store/configureStore";
import {
  NOTIFICATION_CLIENT_ACTIONS,
  NOTIFICATION_VIEW_CLIENT
} from '../../config/notifications';
import { getUserOrders as getUserOrdersApi } from "../../api/orders/orders";
import { ACL, PAGE_CONTEXTS, TRANSLATED_USER_STATUSES } from '../../config/constants';
import { NoRights } from '../../components/layout/Permissions/NoRights';
import { USER_STATUSES } from "../../config/constants";
import { store } from '../../store/store';
import { hideNotification } from '../../store/actions/notification';
import { NOTIFICATION_ERROR } from '../../store/actions/types';
import { getOrganizations as getUserOrganizationsApi } from "../../api/clients/organizations";
import { getCertificatesApi as getUserCertificatesApi } from "../../api/clients/certificates";
import { getCertificateStatusFilterIdentifiers } from "../../config/certificate";
import {
  PageHeader,
  PageSurface,
  PageWorkspace
} from '../../components/layout/Workspace';
import './ViewClient.scss';

const normalizeUserStatus = (user) => {
  if ( ! user?.email_verified_at && user?.status === USER_STATUSES.ACTIVE ) {
    return {
      ...user,
      status: USER_STATUSES.INACTIVE,
    };
  }

  return user;
};

function ViewClientComponent(props) {
  const { userId } = props.match.params;
  const [ user, setUser ] = useState( null );
  const [ orders, setOrders ] = useState( null );
  const [ meta, setMeta ] = useState( null );
  const [ rowsPerPageOrders, setRowsPerPageOrders ] = useState( 10 );

  const [ organizations, setOrganizations ] = useState( null );
  const [ metaOrganizations, setMetaOrganizations ] = useState( null );
  const [ rowsPerPageOrganizations, setRowsPerPageOrganizations ] = useState( 10 );


  const [ certificates, setCertificates ] = useState( null );
  const [ metaCertificates, setMetaCertificates ] = useState( null );
  const [ rowsPerPageCertificates, setRowsPerPageCertificates ] = useState( 10 );

  useEffect( () => {
    getUser( userId );
    getUserOrders( userId, 1 );
    getUserOrganizations( userId, 1 );
    getUserCertificates( userId, 1 );

    return () => {
      if (store.getState().notification.type === NOTIFICATION_ERROR) {
        store.dispatch(hideNotification());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId] );

  /**
   * Get user details
   * @param {string} [userId]
   * @param {boolean} [hideNotification]
   */
  const getUser = (userId, hideNotification) => {
    if (! ACL.CLIENT.USERS.VIEW.hasAccess()) {
      return;
    }

    getUserDetailsApi( {
      userId: userId,
      notificationId: NOTIFICATION_VIEW_CLIENT,
      ...(hideNotification === false ? { hideNotification } : {})
    } ).then( (response) => {
      if (response && response.data && response.data.id) {
        setUser( normalizeUserStatus(response.data) );
      }
    } );
  };

  /**
   * Get orders
   * @param {string} [userId]
   * @param {number} [page]
   * @param {string} [sortField]
   * @param {string} [sortDirection]
   * @param {string} [searchText]
   * @param {boolean} [hideNotification]
   */
  const getUserOrders = (userId, page, sortField, sortDirection, searchText, hideNotification = false) => {
    if (! ACL.CLIENT.ORDERS.LIST.hasAccess()) {
      return;
    }
    getUserOrdersApi( {
      pagination: {
        page: page,
        sort_by: sortField,
        sort: sortDirection,
        q: searchText,
        per_page: rowsPerPageOrders,
        page_context: PAGE_CONTEXTS.ORDERS.LIST,
      },
      userId: userId,
      hideNotification: hideNotification
    } ).then( (response) => {

      setOrders( response.data );
      setMeta( response.meta );
    } );
  };

  /**
   * Update user in state after a success update on server
   * @object value
   */
  const updateUser = (value) => {
    setUser( normalizeUserStatus(value) );
  }

  /**
   * Get organizations
   * @param {string} [userId]
   * @param {number} [page]
   * @param {string} [sortField]
   * @param {string} [sortDirection]
   * @param {string} [searchText]
   * @param {boolean} [hideNotification]
   */
  const getUserOrganizations = (userId, page, sortField, sortDirection, searchText, hideNotification = false) => {
    if (! ACL.CLIENT.USERS.LIST.hasAccess()) {
      return;
    }
    getUserOrganizationsApi( {
      pagination: {
        page: page,
        sort_by: sortField,
        sort: sortDirection,
        q: searchText,
        per_page: rowsPerPageOrganizations,
        page_context: PAGE_CONTEXTS.CLIENT_ORGANIZATION.LIST,
        for_user: userId,
      },
      hideNotification: hideNotification
    } ).then( (response) => {

      setOrganizations( response.data );
      setMetaOrganizations( response.meta );
    } );
  };

  /**
   * Get certificates
   * @param {string} [userId]
   * @param {number} [page]
   * @param {string} [sortField]
   * @param {string} [sortDirection]
   * @param {string} [searchText]
   * @param {boolean} [hideNotification]
   */
  const getUserCertificates = (userId, page, sortField, sortDirection, searchText, filters, hideNotification = false) => {
    if (! ACL.CLIENT.USERS.LIST.hasAccess()) {
      return;
    }

    getUserCertificatesApi( {
      pagination: {
        page: page,
        sort_by: sortField,
        sort: sortDirection,
        q: searchText,
        per_page: rowsPerPageCertificates,
        page_context: PAGE_CONTEXTS.CERTIFICATES.LIST,
        for_user: userId,
        statuses: getCertificateStatusFilterIdentifiers(filters?.statuses),
      },
      hideNotification: hideNotification
    } ).then( (response) => {

      setCertificates( response.data );
      setMetaCertificates( response.meta );
    } );
  };

  const userFullName = user && user.id ? [user.first_name, user.last_name].filter(Boolean).join(' ') : '';
  const backLocation = history.location.state && history.location.state.prevLocation !== null
    ? history.location.state.prevLocation
    : ROUTE_CLIENT_USERS;
  const userStatusModifier = user && user.status === USER_STATUSES.ACTIVE
    ? 'active'
    : user && user.status === USER_STATUSES.DEACTIVATED
      ? 'deactivated'
      : 'muted';
  const pageHeader = (
    <PageHeader
      actions={user && user.id ? (
        <>
          <ResetPasswordClient
            user={user}
            index={1}
            actionCallback={() => null}
          />
          {user.status === USER_STATUSES.DEACTIVATED ? (
            <UnlockClient
              user={user}
              index={1}
              actionCallback={() => getUser( userId, false )}
            />
          ) : (
            <LockClient
              user={user}
              index={1}
              actionCallback={() => getUser( userId, false )}
            />
          )}
          <DeleteClient
            user={user}
            index={1}
            actionCallback={() => history.push( ROUTE_CLIENT_USERS )}
          />
        </>
      ) : null}
      breadcrumbs={(
        <Link className="user-detail-back-link" to={backLocation}>
          <i aria-hidden="true" className="fa fa-arrow-left"/>
          <FormattedMessage id="messages.go_back"/>
        </Link>
      )}
      status={user && user.id ? (
        <span className={`user-detail-status user-detail-status--${userStatusModifier}`}>
          <span aria-hidden="true" className="user-detail-status__dot"/>
          {TRANSLATED_USER_STATUSES[user.status]}
        </span>
      ) : null}
      title={userFullName || <FormattedMessage id="pages.client.title"/>}
    />
  );
  const hasNoPageRights = !ACL.CLIENT.USERS.VIEW.hasAccess() && !ACL.CLIENT.ORDERS.LIST.hasAccess();

  return (
    <DashboardLayout location={props.location} className="dashboard" workspace>
      <PageWorkspace className="user-detail-workspace">
        <div className="user-detail-notifications">
          <Messages notificationId={NOTIFICATION_CLIENT_ACTIONS}/>
          <Messages notificationId={NOTIFICATION_VIEW_CLIENT}/>
        </div>
        {hasNoPageRights ? (
          <>
            {pageHeader}
            <PageSurface>
              <NoRights/>
            </PageSurface>
          </>
        ) : (
          <ClientDetails
            header={pageHeader}
            location={props.location}
            user={user}
            orders={orders}
            updateUser={updateUser}
            getUserOrders={getUserOrders}
            meta={meta}
            rowsPerPageOrders={rowsPerPageOrders}
            setRowsPerPageOrders={setRowsPerPageOrders}
            organizations={organizations}
            getUserOrganizations={getUserOrganizations}
            metaOrganizations={metaOrganizations}
            rowsPerPageOrganizations={rowsPerPageOrganizations}
            setRowsPerPageOrganizations={setRowsPerPageOrganizations}
            certificates={certificates}
            getUserCertificates={getUserCertificates}
            metaCertificates={metaCertificates}
            rowsPerPageCertificates={rowsPerPageCertificates}
            setRowsPerPageCertificates={setRowsPerPageCertificates}
          />
        )}
      </PageWorkspace>
    </DashboardLayout>
  );
}

const mapStateToProps = (state) => ( {} );

const ViewClient = connect( mapStateToProps, null )( ViewClientComponent );

export { ViewClient, ViewClientComponent };
